import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Truck,
  Navigation,
  Loader2,
  ShieldCheck,
  Camera,
  MapPin,
  ChevronRight,
  CheckCircle2,
  CreditCard,
  Eye,
  User,
} from 'lucide-react'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { usePOSStore } from '@/stores/posStore'
import { useFinishEcomSale } from '@/hooks'
import { useSysConfig } from '@/api/config'
import {
  useDeliveryRuns,
  useDeliveryRun,
  type DeliveryRunStop,
} from '@/api/deliveryRuns'
import {
  useSetDeliveryStatus,
  DELIVERY_STATUS,
  type DeliveryStatus,
} from '@/api/receipts'
import { buildStaticMapUrl } from '@/lib/staticMap'
import { runProgress } from '@/lib/fulfillment'
import { formatCurrency } from '@/lib/utils'
import { setDeliveryMode } from '@/lib/deliveryMode'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'

// Driver mobile view (M3). What the driver carries: their assigned runs, and a
// per-stop execution screen (navigate → mark status). Read/act only — runs are
// built and dispatched elsewhere. The mode shell + bottom nav come in M4; for
// now this is reachable at /driver.
//
// v1 uses only what the run-stop payload carries (customer / address / total /
// notes / lat-lng / delivery_status). Items, a Call button, and the COD vs
// prepaid split need extra fields on the stop payload (flagged — small BE add).
// Completion (Delivered) is intentionally not a button here: it happens through
// the POS payment flow, same as everywhere else.

export const Route = createFileRoute('/driver')({
  beforeLoad: () => {
    const { sessionToken, selectedRegister, isAuthenticated } =
      useSessionStore.getState()
    if (!sessionToken) throw redirect({ to: '/auth' })
    if (!selectedRegister) throw redirect({ to: '/select-register' })
    if (!isAuthenticated) throw redirect({ to: '/login' })
  },
  component: DriverPage,
})

function navUrl(stop: DeliveryRunStop): string {
  const dest =
    stop.latitude && stop.longitude
      ? `${stop.latitude},${stop.longitude}`
      : [stop.address, stop.city, stop.province].filter(Boolean).join(', ')
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`
}

// In-transit transitions only (mirrors OrdersTab.deliveryActionsFor — no
// "Delivered", which is payment-driven).
function driverTransitions(
  status: string,
): Array<{ label: string; status: DeliveryStatus; primary?: boolean }> {
  switch (status) {
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return [
        { label: 'Mark failed', status: DELIVERY_STATUS.DELIVERY_FAILED },
        { label: 'Pickup at store', status: DELIVERY_STATUS.PICKUP_AT_STORE },
      ]
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return [
        {
          label: 'Out for delivery again',
          status: DELIVERY_STATUS.OUT_FOR_DELIVERY,
          primary: true,
        },
        { label: 'Pickup at store', status: DELIVERY_STATUS.PICKUP_AT_STORE },
      ]
    default: // none / preparing
      return [
        {
          label: 'Out for delivery',
          status: DELIVERY_STATUS.OUT_FOR_DELIVERY,
          primary: true,
        },
        { label: 'Pickup at store', status: DELIVERY_STATUS.PICKUP_AT_STORE },
      ]
  }
}

function DriverPage() {
  const [section, setSection] = useState<'runs' | 'account'>('runs')
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const showBack = section === 'runs' && selectedRunId !== null
  // Header title: when a run is open, show its name (its ID) instead of a
  // generic "Run". Shares DriverRunView's cached query — no extra fetch.
  const { data: openRun } = useDeliveryRun(selectedRunId)

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <header className="flex-none flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
        {showBack ? (
          <button
            onClick={() => setSelectedRunId(null)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            aria-label="Back to my runs"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <Truck className="w-5 h-5 text-blue-600" />
        )}
        <h1 className="text-lg font-semibold text-gray-900 truncate">
          {section === 'account'
            ? 'Account'
            : showBack
              ? (openRun?.name ?? 'Run')
              : 'Deliveries'}
        </h1>
      </header>

      <main className="flex-1 overflow-y-auto">
        {section === 'account' ? (
          <DriverAccount />
        ) : selectedRunId ? (
          <DriverRunView runId={selectedRunId} />
        ) : (
          <MyRunsList onSelect={setSelectedRunId} />
        )}
      </main>

      {/* Delivery-mode bottom nav. Runs + Account only — the route map lives in
          the run view, and there's no driver Scan action yet (M4 decision). */}
      <nav
        className="flex-none flex border-t border-gray-200 bg-white"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {(
          [
            ['runs', 'Runs', Truck],
            ['account', 'Account', User],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSection(key)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium cursor-pointer ${
              section === key ? 'text-blue-600' : 'text-gray-500'
            }`}
          >
            <Icon className="w-5 h-5" />
            {label}
          </button>
        ))}
      </nav>
    </div>
  )
}

function DriverAccount() {
  const staff = useSessionStore((s) => s.staff)
  const store = useSessionStore((s) => s.selectedStore)
  const navigate = useNavigate()

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Signed in as
        </div>
        <div className="font-semibold text-gray-900 mt-0.5">
          {staff?.full_name ?? '—'}
        </div>
        {store?.name && (
          <div className="text-sm text-gray-500 mt-1">{store.name}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          setDeliveryMode(false)
          navigate({ to: '/pos' })
        }}
        className="w-full h-12 rounded-xl bg-gray-900 active:bg-black text-white font-semibold"
      >
        Exit delivery mode
      </button>
      <p className="text-center text-xs text-gray-400">
        Returns to the POS. This device will open in the POS next time.
      </p>
    </div>
  )
}

function MyRunsList({ onSelect }: { onSelect: (id: number) => void }) {
  const staff = useSessionStore((s) => s.staff)
  const { data: runs, isLoading } = useDeliveryRuns(['open', 'started'])
  // Default to All — until driver assignment is wired, "My deliveries" is
  // usually empty, so showing the store's full active board is the useful
  // landing state.
  const [view, setView] = useState<'mine' | 'all'>('all')

  const allRuns = runs ?? []
  const myRuns = allRuns.filter((r) => r.driver_id === staff?.id)
  const visible = view === 'mine' ? myRuns : allRuns

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-3">
      {/* My / All toggle — a driver can see the whole store's active board,
          not just runs already assigned to them (runs may be dispatched to a
          driver after the fact). Both lists come from the same query. */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
        {(
          [
            { key: 'mine', label: 'My deliveries', n: myRuns.length },
            { key: 'all', label: 'All deliveries', n: allRuns.length },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label} ({t.n})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center py-16 px-8 text-center text-gray-500">
          <Truck className="w-12 h-12 mb-3 text-gray-300" />
          <p className="font-medium">
            {view === 'mine' ? 'No runs assigned to you' : 'No active runs'}
          </p>
          <p className="text-sm">
            {view === 'mine'
              ? 'Switch to All deliveries to see the store’s runs.'
              : 'Runs show up here once they’re created.'}
          </p>
        </div>
      ) : (
        visible.map((run) => (
          <button
            key={run.id}
            onClick={() => onSelect(run.id)}
            className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 active:bg-gray-50 flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="font-mono font-semibold text-gray-900 truncate">
                {run.name}{' '}
                <span className="text-gray-400 font-normal">#{run.id}</span>
              </div>
              <div className="text-sm text-gray-500 mt-0.5">
                {run.stop_count} stop{run.stop_count === 1 ? '' : 's'} ·{' '}
                {run.status === 'started' ? 'In progress' : 'Not started'}
              </div>
              {view === 'all' && (
                <div
                  className={`text-xs mt-0.5 ${
                    run.driver_id ? 'text-gray-400' : 'text-amber-600 font-medium'
                  }`}
                >
                  {run.driver_id
                    ? `Driver: ${run.driver_name ?? (run.driver_id === staff?.id ? 'You' : '—')}`
                    : 'Unassigned'}
                </div>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 flex-none" />
          </button>
        ))
      )}
    </div>
  )
}

function DriverRunView({ runId }: { runId: number }) {
  const { data: run, isLoading } = useDeliveryRun(runId)
  const { data: cfg } = useSysConfig(['kiosk.delivery.google_maps_api_key'])
  const mapsKey = (cfg?.['kiosk.delivery.google_maps_api_key'] as string) || ''
  const setStatus = useSetDeliveryStatus()
  const { showToast } = useUIStore()
  const navigate = useNavigate()
  const { finish: finishEcomSale, isPending: isFinishing } = useFinishEcomSale()
  const setActiveTab = usePOSStore((s) => s.setActiveTab)
  // Default to the auto-computed current stop, but let the driver tap any
  // other stop to work it out of route order.
  const [selectedStopId, setSelectedStopId] = useState<number | null>(null)
  // Read-only order detail opened from the current stop's "View order".
  const [viewReceiptId, setViewReceiptId] = useState<number | null>(null)

  if (isLoading || !run) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  const progress = runProgress(run.stops ?? [])
  const mapUrl = buildStaticMapUrl(run, mapsKey)
  const stops = progress.ordered
  const activeStop =
    (selectedStopId != null
      ? stops.find((s) => s.run_order_id === selectedStopId)
      : null) ?? progress.current

  const setStopStatus = (stop: DeliveryRunStop, status: DeliveryStatus) => {
    setStatus.mutate(
      { receiptId: stop.receipt_id, status },
      {
        onSuccess: () => showToast({ type: 'success', title: 'Delivery updated' }),
        onError: (err) =>
          showToast({
            type: 'error',
            title: 'Could not update',
            message: err instanceof Error ? err.message : undefined,
          }),
      },
    )
  }

  // "Complete order" loads the order into the POS cart (ecom finish-sale) and
  // jumps to the POS so the driver can take payment. Completion is payment-
  // driven — a fully-paid delivery receipt is marked delivered by the backend.
  // An already-paid (locked) receipt can't be re-flipped, so the BE rejects it
  // and we surface the error rather than guessing paid-status driver-side.
  const completeOrder = async (stop: DeliveryRunStop) => {
    try {
      await finishEcomSale(stop.receipt_id)
      // Land on the cart/checkout view — the driver's next step is to take
      // payment, not to add more items.
      setActiveTab('cart')
      navigate({ to: '/pos' })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not load order',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <div className="p-4 space-y-4">
      {/* Progress */}
      <div>
        <div className="flex justify-between text-sm text-gray-600 mb-1.5">
          <span className="font-semibold text-gray-900">
            {progress.complete
              ? 'Run complete'
              : `Stop ${progress.currentIndex + 1} of ${progress.total}`}
          </span>
          <span>{progress.deliveredCount} delivered</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{
              width: `${progress.total ? (progress.deliveredCount / progress.total) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      {mapUrl && (
        <img
          src={mapUrl}
          alt={`Route map for ${run.name}`}
          className="w-full aspect-[2/1] object-cover rounded-xl border border-gray-200 bg-gray-100"
        />
      )}

      {activeStop ? (
        <CurrentStopCard
          stop={activeStop}
          onSetStatus={(status) => setStopStatus(activeStop, status)}
          onComplete={() => completeOrder(activeStop)}
          onViewOrder={() => setViewReceiptId(activeStop.receipt_id)}
          pending={setStatus.isPending}
          completing={isFinishing}
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="font-semibold text-gray-900">All stops handled</p>
          <p className="text-sm text-gray-500 mt-1">
            Take payment in the POS to complete any unpaid deliveries.
          </p>
        </div>
      )}

      {stops.length > 1 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Other stops
          </p>
          <div className="space-y-2">
            {stops.map((stop, idx) => {
              if (stop.run_order_id === activeStop?.run_order_id) return null
              return (
                <button
                  key={stop.run_order_id}
                  type="button"
                  onClick={() => setSelectedStopId(stop.run_order_id)}
                  className="w-full text-left flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 active:bg-gray-50"
                >
                  <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center flex-none">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {stop.customer_name ?? `#${stop.number ?? stop.receipt_id}`}
                    </div>
                    {stop.address && (
                      <div className="text-xs text-gray-500 truncate">
                        {[stop.address, stop.city].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-none" />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {viewReceiptId != null && (
        <OrderDetailModal
          receiptId={viewReceiptId}
          hideAddActions
          onClose={() => setViewReceiptId(null)}
        />
      )}
    </div>
  )
}

function CurrentStopCard({
  stop,
  onSetStatus,
  onComplete,
  onViewOrder,
  pending,
  completing,
}: {
  stop: DeliveryRunStop
  onSetStatus: (status: DeliveryStatus) => void
  onComplete: () => void
  onViewOrder: () => void
  pending: boolean
  completing: boolean
}) {
  const transitions = driverTransitions(stop.delivery_status)

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">
        Current stop
      </span>
      <div className="text-xl font-bold text-gray-900 mt-1">
        {stop.customer_name ?? `#${stop.number ?? stop.receipt_id}`}
      </div>
      {(stop.address || stop.city) && (
        <div className="flex items-start gap-1.5 text-gray-600 mt-1">
          <MapPin className="w-4 h-4 mt-0.5 flex-none" />
          <span>
            {[stop.address, stop.city, stop.province].filter(Boolean).join(', ')}
          </span>
        </div>
      )}
      {stop.delivery_notes && (
        <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
          {stop.delivery_notes}
        </p>
      )}

      {/* Compliance / amount. ID check is always required for cannabis. COD vs
          prepaid needs paid-status on the stop payload, so v1 shows the order
          total to collect-if-unpaid without asserting which. */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 px-2.5 py-1 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" />
          Verify ID
        </span>
        {stop.total != null && (
          <span className="inline-flex items-center text-xs font-semibold text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full">
            {formatCurrency(Number(stop.total))}
          </span>
        )}
        <button
          type="button"
          onClick={onViewOrder}
          className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-blue-600 active:text-blue-700"
        >
          <Eye className="w-4 h-4" />
          View order
        </button>
      </div>

      <div className="mt-4 space-y-2">
        <a
          href={navUrl(stop)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-12 rounded-xl bg-blue-600 active:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2"
        >
          <Navigation className="w-5 h-5" />
          Navigate
        </a>

        {/* Primary action: hand the order to the POS to take payment. Paying
            it in full is what marks the delivery delivered (backend-driven). */}
        <button
          type="button"
          onClick={onComplete}
          disabled={completing}
          className="w-full h-12 rounded-xl bg-green-600 active:bg-green-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <CreditCard className="w-5 h-5" />
          {completing ? 'Loading…' : 'Complete order'}
        </button>

        <div className="flex gap-2">
          {transitions.map((t) => (
            <button
              key={t.label}
              type="button"
              disabled={pending}
              onClick={() => onSetStatus(t.status)}
              className={`flex-1 h-11 rounded-xl font-semibold text-sm disabled:opacity-50 ${
                t.primary
                  ? 'bg-gray-900 active:bg-black text-white'
                  : 'bg-gray-100 active:bg-gray-200 text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled
          className="w-full h-11 rounded-xl bg-gray-50 text-gray-400 font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed"
        >
          <Camera className="w-4 h-4" />
          Proof of delivery · Coming soon
        </button>
      </div>
    </div>
  )
}
